/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { coalesce } from '@gh/core-util';
import { DialogComponentService } from '../../services';
import { TextMessageDialogButton } from '@gh/core-ui';

export type DialogLayoutType = 'flat' | 'catalog' | 'fluid';

/**
 * This directive allows to control closing of dialog when user clicks on backdrop or close button in the dialog header
 */
@Directive({
  selector: 'gh-dialog[close]', // tslint:disable-line:directive-selector
})
export class DialogCloseDirective {
  // tslint:disable-next-line:no-output-named-after-standard-event
  @Output() close = new EventEmitter<void>();
}

@Component({
  selector: 'gh-dialog',
  template: '<ng-content></ng-content>',
  styles: [require('./dialog.component.scss')],
})
export class DialogComponent implements OnInit, OnDestroy {
  @Input() buttons?: TextMessageDialogButton<any>[];
  openTime: number;
  @Input() layout: DialogLayoutType = 'flat';

  /**
   * If dialog is closable it has close icon in the header.
   * If closable property is not set, close buttton will be hidden only when disableClose is set to true
   */
  @Input()
  set closable(closable: boolean) {
    this._closable = coerceBooleanProperty(closable);
  }

  /**
   * By default dialog is considered closable if <code>MatDialogConfig.disableClose</code> was set to true.
   * Developer can overwrite this behavior if <code>closable</code> attribute/input was set for dialog
   */
  get closable(): boolean {
    return coalesce(this._closable, !coalesce(this.dialogRef.disableClose, false));
  }

  private _closable: boolean;

  constructor(private renderer: Renderer2,
              private service: DialogComponentService,
              private elementRef: ElementRef,
              private dialogRef: MatDialogRef<any>,
              @Optional() public closeDialog: DialogCloseDirective) {

  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeyPress(): void {
    if (this.service.isLast(this.openTime)) {
      if (this.buttons && this.buttons.length) {
        const cancelButton = this.buttons.find((button) => button.value === 'cancel');
        const noButton = this.buttons.find((button) => button.value === 'no');
        const closeButton = this.buttons.find((button) => button.value === 'close');
        const param = cancelButton?.value || noButton?.value || closeButton?.value || '';
        this.close(param);
        return;
      }
      this.close();
    }
  }

  ngOnInit(): void {
    this.openTime = new Date().getTime();
    this.service.addDialog(this.openTime);
    if (this.closeDialog) {
      this.dialogRef.backdropClick().subscribe(() => {
        this.closeDialog.close.emit();
      });
    }

    this.renderer.addClass(this.elementRef.nativeElement, `dialog-${this.layout}-layout`);
  }

  ngOnDestroy(): void {
    this.service.removeDialog(this.openTime);
  }

  close(action?: any): void {
    if (!this.closeDialog) {
      this.dialogRef.close(action || undefined);
    } else {
      this.closeDialog.close.emit(action || undefined);
    }
  }
}
